import { useEffect, useMemo, useState } from "react";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import toast, { ToastBar, Toaster } from "react-hot-toast";
import { ErrorBoundary } from "react-error-boundary";
import "./App.css";
import { DarkModeProvider } from "./context/DarkModeContext";
import { SidebarToggleProvider } from "./context/ToggleSideBarContext";
import ErrorFallback from "./components/ui/ErrorFallback/ErrorFallback";
import { TogglProfileDropdownProvider } from "./context/ToggleProfileDropdown";
import { AuthContextProvider } from "./context/AuthContext";
import { ROLE } from "./utils/constants";
import { Modal } from "./components/Modal";
import { CustomerAccountContextProvider } from "./context/CustomerAccountContext";
import Logout from "./pages/Logout/Logout";
import Button from "./components/ui/Button";
import ButtonsContainer from "./components/ui/ButtonsContainer";
import { logoutApi } from "./features/Authentication/useLogout";
import { usePageReloadOnLocalStorageChange } from "./hooks/usePageReloadOnLocalStorageChange";
import { Provider } from "react-redux";
import { store } from "./store";
import { AxiosError } from "axios";
import { ErrorContextProvider } from "./context/ErrorContext";
import LazyLoader from "./components/lazyLoader";
import AdminRoutes from "./routes/AdminRoutes";
import ClientRoutes from "./routes/ClientRoutes";
import SubClientRoutes from "./routes/SubClientRoutes";
import CloseButton from "./components/CloseButton";
import ProtectedRoute from "./routes/ProtectedRoute";
import PublicRoute from "./routes/PublicRoute";
import RestrictedRoute from "./routes/RestrictedRoute";
import AppLoader from "./components/ui/AppLoader";
import { ErrorReportingModal } from "./components/Modals/ErrorReportingModal";
import { ReactQueryProvider } from "context/ReactQueryProvider";

const Login = lazyWithRetry(() => import("./pages/Login/Login"));
const ResetPassword = lazyWithRetry(
  () => import("./features/Authentication/ResetPassword"),
);
const ForgotPassword = lazyWithRetry(
  () => import("./pages/ForgotPassword/ForgotPassword"),
);
const VerifyOtp = lazyWithRetry(() => import("./pages/VerifyOtp/VerifyOtp"));
const SomeThingWentWrong = lazyWithRetry(
  () => import("./pages/SomethingWentWrong/SomeThingWentWrong"),
);

function App() {
  // Memoize localStorage parsing to prevent re-parsing on every render
  const localUser = useMemo(
    () => JSON.parse(localStorage.getItem("user") || "{}"),
    [],
  );
  const clientId = useMemo(
    () => localUser?.clientId?.toString() || "-1",
    [localUser],
  );

  const [openModal, setOpenModal] = useState(false);
  const [warningMsg, setWarningMsg] = useState("");
  const [isOnline, setIsOnline] = useState(true);
  const [isReportErrorLoading, setIsReportErrorLoading] = useState(false);

  const enteredUrl = window.location.href;
  const enteredUrlClientId = useMemo(() => {
    return !window.location.search.includes("loginUrl") &&
      window.location.search.includes("token=")
      ? atob(window.location.search?.split("=")?.[1])
      : "-1";
  }, []);

  usePageReloadOnLocalStorageChange();

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  useEffect(() => {
    if (
      localUser &&
      localUser.hasOwnProperty("isAuthenticated") &&
      clientId !== enteredUrlClientId &&
      window.location.pathname === "/"
    ) {
      openWarningModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openWarningModal = () => {
    setOpenModal(true);
    setWarningMsg("A user is already active, please logout first!!");
  };

  const closeModal = () => {
    setOpenModal(false);
    setWarningMsg("");
  };

  const removeUserFromLocalStorage = () => {
    localStorage.removeItem("user");
    sessionStorage.clear();
  };

  const handleLogout = () => {
    logoutApi()
      .then(() => {
        window.location.replace(enteredUrl);
        removeUserFromLocalStorage();
      })
      .catch((err) => {
        if (err instanceof AxiosError) {
          if (err?.response?.status === 401 || err?.response?.status === 403) {
            window.location.replace(enteredUrl);
            removeUserFromLocalStorage();
          }
        }
      })
      .finally(() => {
        setOpenModal(false);
      });
  };

  return (
    <>
      {isReportErrorLoading && <AppLoader />}
      <Modal
        open={openModal}
        titleContent={<p>Already active user</p>}
        content={<p className="bold__text">{warningMsg}</p>}
        actions={
          <ButtonsContainer>
            <Button buttonType="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button buttonType="primary" type="button" onClick={handleLogout}>
              Logout
            </Button>
          </ButtonsContainer>
        }
        closeFn={closeModal}
        escFn={closeModal}
        closeIcon={<CloseButton onClose={closeModal} />}
      />

      {openModal ? null : (
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onReset={() =>
            window.location.replace(localStorage.getItem("userLoginUrl") || "/")
          }
        >
          <DarkModeProvider>
            <ErrorContextProvider>
              <ReactQueryProvider>
                <AuthContextProvider>
                  <TogglProfileDropdownProvider>
                    <Provider store={store}>
                      <CustomerAccountContextProvider>
                        <ErrorReportingModal />
                        <SidebarToggleProvider>
                          <BrowserRouter>
                            <Routes>
                              <Route path="/">
                                <Route
                                  path="/admin/*"
                                  element={<AdminRoutes />}
                                />
                                <Route
                                  path="/subclient/*"
                                  element={<SubClientRoutes />}
                                />
                                <Route
                                  path="/client/*"
                                  element={<ClientRoutes />}
                                />
                                <Route
                                  path="logout"
                                  element={
                                    <ProtectedRoute
                                      roles={[
                                        ROLE.admin,
                                        ROLE.client,
                                        ROLE.subClient,
                                      ]}
                                    >
                                      <Logout />
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  index
                                  element={
                                    <LazyLoader>
                                      <PublicRoute>
                                        <Login />
                                      </PublicRoute>
                                    </LazyLoader>
                                  }
                                />
                                <Route
                                  path="reset-password"
                                  element={
                                    <LazyLoader>
                                      <PublicRoute>
                                        <ResetPassword />
                                      </PublicRoute>
                                    </LazyLoader>
                                  }
                                />
                                <Route
                                  path="forgot-password"
                                  element={
                                    <LazyLoader>
                                      <PublicRoute>
                                        <ForgotPassword />
                                      </PublicRoute>
                                    </LazyLoader>
                                  }
                                />
                                <Route
                                  path="verify-otp"
                                  element={
                                    <LazyLoader>
                                      <RestrictedRoute>
                                        <VerifyOtp />
                                      </RestrictedRoute>
                                    </LazyLoader>
                                  }
                                />
                              </Route>
                              <Route
                                path="/something-went-wrong"
                                element={
                                  <LazyLoader>
                                    <SomeThingWentWrong />
                                  </LazyLoader>
                                }
                              />
                              {/* Fallback Routes */}
                              <Route
                                path="*"
                                element={
                                  <LazyLoader>
                                    <SomeThingWentWrong />
                                  </LazyLoader>
                                }
                              />
                            </Routes>
                          </BrowserRouter>
                        </SidebarToggleProvider>
                        <Toaster
                          position="top-center"
                          gutter={12}
                          containerStyle={{ margin: "8px" }}
                          toastOptions={{
                            success: {
                              duration: 10000,
                              icon: "✅",
                            },
                            error: {
                              duration: 10000,
                              icon: "⛔",
                            },
                            style: {
                              fontSize: "var(--font-xs)",
                              maxWidth: "600px",
                              padding: "var(--p-16)",
                            },
                          }}
                        >
                          {(t) => (
                            <ToastBar toast={t}>
                              {({ icon, message }) => (
                                <>
                                  {icon}
                                  {message}
                                  {t.type !== "loading" && (
                                    <button
                                      onClick={() => toast.dismiss(t.id)}
                                      style={{
                                        fontSize: "var(--font-md)",
                                        color: "gray",
                                      }}
                                    >
                                      x
                                    </button>
                                  )}
                                </>
                              )}
                            </ToastBar>
                          )}
                        </Toaster>
                      </CustomerAccountContextProvider>
                    </Provider>
                  </TogglProfileDropdownProvider>
                </AuthContextProvider>
              </ReactQueryProvider>
            </ErrorContextProvider>
          </DarkModeProvider>
        </ErrorBoundary>
      )}
    </>
  );
}

export default App;
